import { useLazyProductSearchQuery } from '@api/product.api';
import { ProductFilterChange, ProductSorting } from '@api/types';

import { useAppDispatch, useAppSelector } from '@hooks/redux/store.hooks';
import { setCategoryWorking } from '@store/catalog/catalog.slice';
import { extractFacetKeysFromParams, extractFilterValue } from '@utils/products.utils';
import { useCallback, useMemo } from 'react';

export const INITIAL_CATEGORY_STATE = {
  offset: 0,
  limit: 12,
  sorting: 'PriceAsc' as ProductSorting,
  filters: [],
  itemPerPage: 12,
};

type CategoryHookType = {
  handleFilterChange: (filter: ProductFilterChange) => void;
  handleSortingChange: (sortingOption: string) => void;
  handlePaginationChange: (page: number) => void;
  handleClearFilters: () => void;
};

type SearchParamsUpdate = {
  key: string;
  value: string;
  shouldDeletePage?: boolean;
};

export const useCategory = (): CategoryHookType => {
  const categoryLink = useAppSelector((state) => state.catalog.categoryLink);
  const allFilters = useAppSelector((state) => state.catalog.filters);
  const dispatch = useAppDispatch();

  const [triggerGetProducts] = useLazyProductSearchQuery();

  const searchParams = new URLSearchParams(window.location.search);
  const currentSortingParam =
    (searchParams.get('sorting') as ProductSorting) || INITIAL_CATEGORY_STATE.sorting;
  const currentPage = Number(searchParams.get('page')) || 1;

  const updateSearchParams = useCallback(({ key, value, shouldDeletePage }: SearchParamsUpdate) => {
    const url = new URL(window.location.href);
    if (shouldDeletePage) {
      url.searchParams.delete('page');
    }
    if (value === '') {
      url.searchParams.delete(key);
      window.history.pushState({}, '', url);
      return;
    }
    url.searchParams.set(key, value);
    window.history.pushState({}, '', url);
  }, []);

  const handleFilterChange = useCallback(
    ({ fieldValue, fieldLabel }: ProductFilterChange) => {
      dispatch(setCategoryWorking(true));
      updateSearchParams({
        key: fieldLabel,
        value: extractFilterValue(fieldValue),
        shouldDeletePage: true,
      });
      triggerGetProducts({
        offset: INITIAL_CATEGORY_STATE.offset,
        limit: INITIAL_CATEGORY_STATE.itemPerPage,
        sorting: currentSortingParam,
        categoryLink,
        filters: extractFacetKeysFromParams(allFilters),
      }).then(() => {
        dispatch(setCategoryWorking(false));
      });
    },
    [
      updateSearchParams,
      triggerGetProducts,
      categoryLink,
      allFilters,
      currentSortingParam,
      dispatch,
    ]
  );

  const handleSortingChange = useCallback(
    (sortingOption: string) => {
      dispatch(setCategoryWorking(true));
      updateSearchParams({ key: 'sorting', value: sortingOption });
      triggerGetProducts({
        offset: (currentPage - 1) * INITIAL_CATEGORY_STATE.itemPerPage,
        limit: INITIAL_CATEGORY_STATE.itemPerPage,
        sorting: sortingOption as ProductSorting,
        categoryLink,
        filters: extractFacetKeysFromParams(allFilters),
      }).then(() => {
        dispatch(setCategoryWorking(false));
      });
    },
    [updateSearchParams, triggerGetProducts, categoryLink, allFilters, currentPage, dispatch]
  );

  const handlePaginationChange = useCallback(
    (pageNumber: number) => {
      dispatch(setCategoryWorking(true));
      const pageOffset = (pageNumber - 1) * INITIAL_CATEGORY_STATE.itemPerPage;
      updateSearchParams({ key: 'page', value: pageNumber.toString() });
      triggerGetProducts({
        offset: pageOffset,
        limit: INITIAL_CATEGORY_STATE.itemPerPage,
        sorting: currentSortingParam,
        categoryLink,
        filters: extractFacetKeysFromParams(allFilters),
      }).then(() => {
        dispatch(setCategoryWorking(false));
      });
    },
    [
      updateSearchParams,
      triggerGetProducts,
      categoryLink,
      allFilters,
      currentSortingParam,
      dispatch,
    ]
  );

  const handleClearFilters = useCallback(() => {
    dispatch(setCategoryWorking(true));
    const url = new URL(window.location.href);
    url.search = '';
    window.history.pushState({}, '', url);
    triggerGetProducts({
      offset: INITIAL_CATEGORY_STATE.offset,
      limit: INITIAL_CATEGORY_STATE.itemPerPage,
      sorting: 'PriceAsc',
      categoryLink,
      filters: [],
    }).then(() => {
      dispatch(setCategoryWorking(false));
    });
  }, [categoryLink, triggerGetProducts, dispatch]);

  const value = useMemo(
    () => ({
      handleFilterChange,
      handleSortingChange,
      handlePaginationChange,
      handleClearFilters,
    }),
    [handleFilterChange, handleSortingChange, handlePaginationChange, handleClearFilters]
  );

  return value;
};
